import { useEffect, useRef, useState } from "react";
import { Badge, Card } from "@vb/ui";
import { ROUND_MAX_MS } from "@vb/engine";
import { VaultDoor } from "./cinematic/VaultDoor.js";
import { liveVaultState } from "./cinematic/liveVaultState.js";

const CASHOUT_MS = 5400;
const CRACK_TIMES = [1400, 2700, 4000, 5000];
const CASHED_HOLD_MS = 1400;
const RESET_MS = 800;
const CYCLE_MS = CASHOUT_MS + CASHED_HOLD_MS + RESET_MS;

/**
 * Scripted demo loop for the marketing home. Cycles through a 5.4s climb,
 * cashout hold, brief reset — repeat. The visible animation is the locks
 * cracking on the vault face; the live multiplier number is intentionally
 * suppressed here so the door art owns the marketing card.
 */
export function FlagshipPreview() {
  const [cracked, setCracked] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const start = performance.now();
    const tick = (t: number) => {
      const elapsed = (t - start) % CYCLE_MS;
      if (elapsed < CASHOUT_MS) {
        setCracked(CRACK_TIMES.filter((c) => c <= elapsed).length);
      } else if (elapsed < CASHOUT_MS + CASHED_HOLD_MS) {
        setCracked(4);
      } else {
        setCracked(0);
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <Card
      style={{
        padding: 0,
        overflow: "hidden",
        position: "relative",
        minHeight: 480,
        background:
          "radial-gradient(ellipse at 30% 30%, rgba(0,229,255,0.12), transparent 55%), var(--bg-2)",
      }}
    >
      <div
        style={{
          padding: "20px 24px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Badge kind="live">FLAGSHIP</Badge>
          <span className="label">Vault Breaker · 30s round</span>
        </div>
        <span className="mono" style={{ color: "var(--fg-3)", fontSize: 11 }}>
          scripted loop · not a live round
        </span>
      </div>

      {/* Stage block: centered vault, no multiplier overlay. LOCKS row sits
          beneath as the only live readout. */}
      <div style={{ padding: "8px 24px 20px" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
          <div style={{ width: 360, height: 360, position: "relative" }}>
            <VaultDoor state={liveVaultState(0, "betting", cracked, 1)} size={360} />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
            marginTop: 8,
          }}
        >
          <span className="label">LOCKS</span>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: 999,
                background: i < cracked ? "var(--gold-bright)" : "var(--bg-3)",
                border: i < cracked ? "1px solid #8A6426" : "1px solid var(--line-strong)",
                boxShadow: i < cracked ? "0 0 8px rgba(245,197,109,0.6)" : "none",
              }}
            />
          ))}
        </div>
      </div>

      <div
        style={{
          padding: "12px 24px",
          borderTop: "1px solid var(--line)",
          background: "var(--bg-1)",
          fontSize: 12,
          fontFamily: "var(--font-mono)",
          color: "var(--fg-3)",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>house edge · 1.0%</span>
        <span>round · {Math.round(ROUND_MAX_MS / 1000)}s</span>
        <span>RTP · 99.0%</span>
      </div>
    </Card>
  );
}
